import type { AppTranslations } from '../i18n/app'

type TextImportStrings = AppTranslations['textImport']

/**
 * The message shown when POST /text-import/upload/ fails.
 *
 * Chosen by the error envelope's `code`, never from its `detail` text, which
 * is English and written for developers. An unknown code, or an error with no
 * JSON body, falls back to a generic message; no response at all is a
 * network failure.
 */
export function fileUploadErrorMessage(err: unknown, filename: string, ti: TextImportStrings): string {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } } | null)?.response
  if (!response) return ti.uploadErrNetwork

  const detail = response.data?.detail
  const code =
    detail && typeof detail === 'object' && typeof (detail as { code?: unknown }).code === 'string'
      ? (detail as { code: string }).code
      : null

  switch (code) {
    case 'EMPTY_FILE':
      return ti.uploadErrNoText(ti.takePhoto, ti.fromGallery)
    case 'FILE_CORRUPTED':
      return ti.uploadErrCorrupted(formatName(filename))
    case 'FILE_ENCRYPTED':
      return ti.uploadErrEncrypted
    case 'UNSUPPORTED_FILE_TYPE':
      return ti.uploadErrUnsupported
    case 'FILE_TOO_LARGE':
      return ti.uploadErrTooLarge
    case 'RATE_LIMITED':
      return ti.uploadErrRateLimited
  }
  // nginx answers an oversized body itself, as HTML with no envelope.
  if (response.status === 413) return ti.uploadErrTooLarge
  return ti.uploadErrGeneric
}

// "lesson.pdf" → "PDF". Used inside a sentence, so no leading dot.
function formatName(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot >= 0 ? filename.slice(dot + 1).toUpperCase() : filename
}
